/**
 * Web-side wrapper around the `fetchBacklogAttachment` callable. The
 * Backlog API key lives in Secret Manager — the WBS panel asks the
 * backend to proxy the image bytes and returns them as base64 so the
 * panel can render `<img src="data:...">` for `![image][...]` tokens
 * in description and comment bodies.
 *
 * Test seam via `setAttachmentRunnerForTests` mirrors `backlogSearch.ts`.
 */
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirebaseApp } from './firebase';

export interface FetchBacklogAttachmentBody {
  projectId: string;
  issueKey: string;
  attachmentName: string;
}

export type FetchBacklogAttachmentResult =
  | {
      ok: true;
      contentType: string;
      base64: string;
    }
  | {
      ok: false;
      reason:
        | 'config-missing'
        | 'secret-error'
        | 'backlog-error'
        | 'not-found'
        | 'not-image'
        | 'too-large';
      message: string;
    };

export type AttachmentRunner = (
  body: FetchBacklogAttachmentBody,
) => Promise<FetchBacklogAttachmentResult>;

let _runner: AttachmentRunner | null = null;

/** Test seam — swap in a mock runner. Returns the previous runner. */
export function setAttachmentRunnerForTests(
  runner: AttachmentRunner | null,
): AttachmentRunner | null {
  const prev = _runner;
  _runner = runner;
  return prev;
}

function defaultRunner(): AttachmentRunner {
  return async (body) => {
    const fns = getFunctions(getFirebaseApp(), 'asia-northeast1');
    const callable = httpsCallable<FetchBacklogAttachmentBody, FetchBacklogAttachmentResult>(
      fns,
      'fetchBacklogAttachment',
    );
    const res = await callable(body);
    return res.data;
  };
}

export async function fetchBacklogAttachment(
  body: FetchBacklogAttachmentBody,
): Promise<FetchBacklogAttachmentResult> {
  const run = _runner ?? defaultRunner();
  return await run(body);
}
